import { DOCUMENT } from "@angular/common";
import { Injectable, inject } from "@angular/core";
import { Logger } from "@dtm-frontend/shared/utils";
import { IMessage, RxStompState } from "@stomp/rx-stomp";
import { Observable, pipe, share } from "rxjs";
import { filter, map } from "rxjs/operators";
import { RxStompService } from "./websocket-connection-status.service";
import { WebsocketConnectionStatus, WebsocketMessage } from "./websocket.models";

@Injectable()
export class WebsocketService {
    private readonly document = inject(DOCUMENT);
    private readonly sharedWatchers: Map<string, Observable<WebsocketMessage>> = new Map();

    constructor(private readonly rxStompService: RxStompService) {
        this.rxStompService.webSocketErrors$.subscribe((error) => {
            Logger.captureMessage("WebSocket error", {
                extra: {
                    error,
                },
                level: "error",
            });
        });

        this.document.defaultView?.addEventListener("offline", async () => {
            await this.rxStompService.deactivate();
            this.rxStompService.activate();
        });
    }

    public readonly connectionStatus$ = this.rxStompService.connectionState$.pipe(
        map((state) => {
            switch (state) {
                case RxStompState.OPEN:
                    return WebsocketConnectionStatus.Connected;
                case RxStompState.CLOSED:
                    return WebsocketConnectionStatus.Disconnected;
                case RxStompState.CONNECTING:
                    return WebsocketConnectionStatus.Connecting;
                case RxStompState.CLOSING:
                    return WebsocketConnectionStatus.Disconnecting;
            }
        })
    );

    public watchTopic(topicName: string, eventTypes?: string[]): Observable<WebsocketMessage> {
        const sharedWatcher = this.sharedWatchers.get(topicName);
        if (sharedWatcher) {
            return sharedWatcher.pipe(this.filterByEvents(eventTypes));
        }

        const watcher = this.rxStompService.watch(topicName).pipe(share());
        this.sharedWatchers.set(topicName, watcher);

        return watcher.pipe(this.filterByEvents(eventTypes));
    }

    private filterByEvents(eventTypes?: string[]) {
        return pipe(
            filter((message: IMessage) => {
                if (!eventTypes) {
                    return true;
                }

                return eventTypes.includes(message.headers["event-type"]);
            })
        );
    }
}
